import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Play, Loader2, Send, Check, DoorOpen, Eye, Copy, Crosshair, Clock, Swords } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRoom } from "@/context/RoomContext";
import { QUICK_PHRASES, EMOTES } from "@/components/CoopChat";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const MODE_LABEL = { coop: "Co-op", pvp: "PvP", party: "Party", open: "Open" };

export default function LobbyDialog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    activeRoom: room, openLobby, setOpenLobby,
    sendChat, toggleReady, startMatch, leaveRoom,
  } = useRoom();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [mmode, setMmode] = useState("deathmatch");
  const [fragLimit, setFragLimit] = useState(15);
  const [goalTarget, setGoalTarget] = useState(5);
  const [timeLimit, setTimeLimit] = useState(120);
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [room?.chat?.length]);

  if (!room || !openLobby) return null;

  const isFps = room.game_template === "fps3d";
  const isBattle = room.game_template === "battle3d";
  const isSoccer = room.game_template === "soccer3d";
  const send = async () => { const b = msg.trim(); if (!b) return; setMsg(""); await sendChat(b); };
  const start = async () => {
    setBusy(true);
    let opts = {};
    if (isFps) opts = { mode: mmode, frag_limit: fragLimit, time_limit: timeLimit };
    else if (isBattle) opts = { frag_limit: fragLimit, time_limit: timeLimit };
    else if (isSoccer) opts = { goal_target: goalTarget, time_limit: timeLimit };
    const data = await startMatch(opts);
    setBusy(false);
    if (data) { toast.success("Match started!"); setOpenLobby(false); navigate(`/app/coop/${room.id}`); }
  };
  const leave = async () => { await leaveRoom(); };
  const copyCode = () => { navigator.clipboard?.writeText(room.code); toast.success("Code copied!"); };
  const copyInvite = () => { navigator.clipboard?.writeText(`${window.location.origin}/app/coop/${room.id}`); toast.success("Invite link copied — share it to play together!"); };

  const members = room.members || [];
  const spectators = room.spectators || [];
  const isHost = room.is_host;
  const meReady = room.me?.ready;
  const isSpectator = room.me?.role === "spectator";

  return (
    <Dialog open={openLobby} onOpenChange={(o) => !o && setOpenLobby(false)}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="lobby-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="text-2xl">{room.game_cover}</span> {room.game_title}
            <Badge variant={room.status === "playing" ? "default" : "secondary"} className="gap-1">
              {room.status === "playing" ? <><Play className="w-2.5 h-2.5" /> Playing</> : "Lobby"}
            </Badge>
            <button onClick={copyCode} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1 text-sm font-mono tracking-widest hover:bg-[hsl(var(--accent))]" data-testid="room-code-copy">
              {room.code} <Copy className="w-3.5 h-3.5" />
            </button>
            <button onClick={copyInvite} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] px-3 py-1 text-xs font-semibold hover:bg-[hsl(var(--primary))]/10" data-testid="room-invite-copy">
              <Copy className="w-3.5 h-3.5" /> Invite link
            </button>
          </DialogTitle>
          <DialogDescription>{MODE_LABEL[room.mode] || room.mode} · {room.player_count} online · max {room.max_players}</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 flex-1 min-h-0">
          <div className="flex flex-col min-h-0">
            <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Players ({members.length})</div>
            <div className="space-y-1.5 overflow-y-auto pr-1" data-testid="lobby-members">
              {members.map((m) => (
                <div key={m.user_id} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm">
                  <span className="text-lg">{m.avatar}</span>
                  <span className="truncate flex-1">{m.name}{m.user_id === user?.id && " (you)"}</span>
                  {m.role === "host" && <Crown className="w-4 h-4 text-amber-400" title="Host" />}
                  {m.online ? <span className="w-2 h-2 rounded-full bg-emerald-500" title="Online" /> : <span className="w-2 h-2 rounded-full bg-zinc-500" title="Away" />}
                  {m.ready ? <Badge variant="secondary" className="text-[10px] gap-1"><Check className="w-2.5 h-2.5" /> Ready</Badge> : <Badge variant="outline" className="text-[10px]">Not ready</Badge>}
                </div>
              ))}
              {spectators.length > 0 && (
                <div className="pt-2">
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1 flex items-center gap-1"><Eye className="w-3 h-3" /> Spectators</div>
                  {spectators.map((m) => (
                    <div key={m.user_id} className="text-xs text-[hsl(var(--muted-foreground))] px-2 py-1">{m.avatar} {m.name}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col min-h-0 border-l border-[hsl(var(--border))] md:pl-4">
            <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Lobby chat</div>
            <div ref={chatRef} className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[140px]" data-testid="lobby-chat">
              {(room.chat || []).length === 0 ? (
                <div className="text-xs text-[hsl(var(--muted-foreground))] py-4 text-center">Say hi to your squad 👋</div>
              ) : (room.chat || []).map((c) => (
                <div key={c.id} className="text-sm">
                  <span className="font-semibold">{c.avatar} {c.name}:</span> <span className="text-[hsl(var(--muted-foreground))]">{c.body}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 mt-2" data-testid="lobby-quick-chat">
              {QUICK_PHRASES.slice(0, 4).map((p, i) => (
                <button key={p} type="button" onClick={() => sendChat(p, "quick")} data-testid={`lobby-quick-${i}`}
                  className="px-2 py-0.5 rounded-full text-[11px] border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] transition-colors">{p}</button>
              ))}
              {EMOTES.slice(0, 5).map((e, i) => (
                <button key={e} type="button" onClick={() => sendChat(e, "emote")} data-testid={`lobby-emote-${i}`}
                  className="w-6 h-6 grid place-items-center rounded-md text-sm hover:bg-[hsl(var(--accent))] transition-colors">{e}</button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mt-2">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message…" maxLength={500} data-testid="chat-input" />
              <Button type="submit" size="icon" className="rounded-full shrink-0" data-testid="chat-send"><Send className="w-4 h-4" /></Button>
            </form>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 flex-wrap">
          {isFps && isHost && room.status === "lobby" && (
            <div className="w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 mb-1" data-testid="fps-match-settings">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5" /> Deathmatch settings</div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant={mmode === "deathmatch" ? "default" : "outline"} className="rounded-full gap-1.5" onClick={() => setMmode("deathmatch")} data-testid="fps-mode-ffa"><Swords className="w-3.5 h-3.5" /> Free-for-all</Button>
                <Button type="button" size="sm" variant={mmode === "team" ? "default" : "outline"} className="rounded-full gap-1.5" onClick={() => setMmode("team")} data-testid="fps-mode-team">🔴🔵 Team (Red vs Blue)</Button>
                <div className="flex items-center gap-1.5 text-sm ml-1"><Crosshair className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  <select value={fragLimit} onChange={(e) => setFragLimit(Number(e.target.value))} className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm" data-testid="fps-frag-limit">
                    {[10, 15, 20, 30, 50].map((n) => <option key={n} value={n}>{n} kills</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1.5 text-sm"><Clock className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  <select value={timeLimit} onChange={(e) => setTimeLimit(Number(e.target.value))} className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm" data-testid="fps-time-limit">
                    {[60, 90, 120, 180, 300].map((n) => <option key={n} value={n}>{n < 60 ? n + "s" : (n / 60) + " min"}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Round ends at the frag limit or when time runs out — whichever comes first.</p>
            </div>
          )}
          {isBattle && isHost && room.status === "lobby" && (
            <div className="w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 mb-1" data-testid="battle-match-settings">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">🪂 Battle Royale settings</div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5 text-sm"><Crosshair className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  <select value={fragLimit} onChange={(e) => setFragLimit(Number(e.target.value))} className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm" data-testid="battle-frag-limit">
                    {[5, 8, 10, 15, 20].map((n) => <option key={n} value={n}>{n} eliminations</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1.5 text-sm"><Clock className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  <select value={timeLimit} onChange={(e) => setTimeLimit(Number(e.target.value))} className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm" data-testid="battle-time-limit">
                    {[60, 90, 120, 180, 300].map((n) => <option key={n} value={n}>{n < 60 ? n + "s" : (n / 60) + " min"}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Free-for-all — blast rival players in the shrinking zone. Most eliminations when the frag limit or time is hit wins.</p>
            </div>
          )}
          {isSoccer && isHost && room.status === "lobby" && (
            <div className="w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 mb-1" data-testid="soccer-match-settings">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">⚽ Team Soccer settings</div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5 text-sm"><span aria-hidden>🥅</span>
                  <select value={goalTarget} onChange={(e) => setGoalTarget(Number(e.target.value))} className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm" data-testid="soccer-goal-target">
                    {[3, 5, 7, 10].map((n) => <option key={n} value={n}>{n} team goals</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1.5 text-sm"><Clock className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  <select value={timeLimit} onChange={(e) => setTimeLimit(Number(e.target.value))} className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm" data-testid="soccer-time-limit">
                    {[60, 90, 120, 180, 300].map((n) => <option key={n} value={n}>{n < 60 ? n + "s" : (n / 60) + " min"}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Red vs Blue — everyone plays their own pitch; your goals add to your team's total. First team to the target (or ahead at time) wins.</p>
            </div>
          )}
          <Button variant="outline" className="rounded-full" onClick={leave} data-testid="leave-room-btn"><DoorOpen className="w-4 h-4" /> Leave</Button>
          {!isSpectator && room.status === "lobby" && (
            <Button variant={meReady ? "secondary" : "outline"} className="rounded-full" onClick={toggleReady} data-testid="ready-btn">
              <Check className="w-4 h-4" /> {meReady ? "Ready ✓" : "Mark ready"}
            </Button>
          )}
          {room.status === "playing" ? (
            <Button className="rounded-full" onClick={() => { setOpenLobby(false); navigate(`/app/coop/${room.id}`); }} data-testid="enter-game-btn"><Play className="w-4 h-4" /> Enter game</Button>
          ) : isHost ? (
            <Button className="rounded-full" onClick={start} disabled={busy} data-testid="start-room-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Start match
            </Button>
          ) : (
            <Button className="rounded-full" disabled variant="secondary">Waiting for host…</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
